/**
 * Dirty form guard: a module-level registry of "is dirty" checkers.
 *
 * Pages call `useDirtyGuard(isDirtyRef, 'page description')` on mount.
 * The composable registers the checker and removes it on unmount.
 * `confirmDiscard()` opens a Dialog (rendered by DirtyGuardDialog.vue, mounted once in AppShell)
 * when any registered checker returns true, otherwise resolves true immediately.
 */

import { getCurrentInstance, onUnmounted, ref, type Ref } from 'vue'

// ---------------------------------------------------------------------------
// Internal registry
// ---------------------------------------------------------------------------

interface DirtyChecker {
  /** A reactive ref or a function returning boolean. */
  isDirty: Ref<boolean> | (() => boolean)
  /** Human-readable description of what has unsaved changes. */
  what: string
}

const checkers: DirtyChecker[] = []

let resolveFn: ((value: boolean) => void) | null = null
let pending: Promise<boolean> | null = null
const dialogOpen = ref(false)
const dialogMessage = ref('')

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Register a dirty checker for the current component lifecycle.
 * Call in `onMounted` (or top-level `<script setup>`); the checker is
 * automatically removed when the component unmounts.
 *
 * @param isDirty - A `Ref<boolean>` or a function `() => boolean`
 * @param what - Optional description, e.g. "appointment form", "POS ticket"
 */
export function useDirtyGuard(
  isDirty: Ref<boolean> | (() => boolean),
  what = 'this page',
): void {
  const checker: DirtyChecker = { isDirty, what }
  checkers.push(checker)

  // Auto-unregister on unmount
  const unregister = () => {
    const idx = checkers.indexOf(checker)
    if (idx >= 0) checkers.splice(idx, 1)
  }

  // Only inside a component setup; otherwise the caller must unregister.
  if (getCurrentInstance()) onUnmounted(unregister)
}

/**
 * True when any registered checker reports unsaved changes.
 * The router guard and the unload guard consult this before prompting.
 */
export function hasDirtyChanges(): boolean {
  return checkers.some((c) =>
    typeof c.isDirty === 'function' ? c.isDirty() : c.isDirty.value,
  )
}

/**
 * Check all registered dirty checkers. If none are dirty, resolves true immediately.
 * Otherwise opens the confirmation dialog and resolves with the user's choice.
 */
export async function confirmDiscard(): Promise<boolean> {
  // Fast path: nothing dirty
  const dirty = checkers.find((c) =>
    typeof c.isDirty === 'function' ? c.isDirty() : c.isDirty.value,
  )
  if (!dirty) return true

  // Something is dirty -> open dialog
  dialogMessage.value = `You have unsaved changes on ${dirty.what}. Continuing will discard them.`
  dialogOpen.value = true

  // A second call while the prompt is already open shares the same answer.
  // Clicking the prompt counts as an "outside" interaction for the dialog
  // underneath, which asks again; cancelling the first prompt there made
  // "Discard changes" reopen the prompt forever, so the dialog never closed.
  if (pending) return pending
  pending = new Promise<boolean>((resolve) => {
    resolveFn = (v: boolean) => {
      pending = null
      resolve(v)
    }
  })
  return pending
}

/** Called by DirtyGuardDialog.vue when the user clicks "Discard and switch". */
export function confirmDialog(result: boolean): void {
  dialogOpen.value = false
  if (resolveFn) {
    resolveFn(result)
    resolveFn = null
  }
}

// ---------------------------------------------------------------------------
// Unload guard (tab close / refresh)
// ---------------------------------------------------------------------------

/**
 * A caller that has ALREADY asked the user (for example a company switch that
 * ends in `router.go(0)`) sets this so the reload does not prompt twice.
 */
let unloadApproved = false

/** Let the next unload through without a second prompt. */
export function allowUnloadOnce(): void {
  unloadApproved = true
}

/**
 * `beforeunload` cannot host frappe-ui's Dialog, so the browser's own
 * confirmation is the only option for a refresh / tab close. Registered once
 * at module load and inert until a checker reports unsaved changes.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (event) => {
    if (unloadApproved) {
      unloadApproved = false
      return
    }
    if (!hasDirtyChanges()) return
    event.preventDefault()
    event.returnValue = ''
  })
}


/** Reactive state for the dialog component. */
export const dirtyGuardDialogState = {
  open: dialogOpen,
  message: dialogMessage,
  confirm: confirmDialog,
}

// Needed so this file is treated as a module
export {}
